import { useState } from 'react';

import StripeCardModal from '@/components/stripe/StripeCardModal';
import {
  getOrganizationCapabilitiesApi,
  stripeBillingPortalApi,
  stripeSetupIntentApi,
  stripeSubscribeApi,
} from '@/lib/api/organizationsApi';
import { formatDateAbbreviatedFr } from '@shared';
import { FORFAIT_ENABLED, ORGA_FORFAIT_ANNUAL_TTC_CENTS, ORGA_FORFAIT_MONTHLY_TTC_CENTS } from '@shared/constants';
import { SubPlan, SubscriptionStatus } from '@shared/enums/domain.enum';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink, Loader2, Sparkles, Zap } from 'lucide-react';

const SUB_STATUS_CONFIG: Record<SubscriptionStatus, { label: string; badge: string }> = {
  [SubscriptionStatus.ACTIVE]: { label: 'Actif', badge: 'bg-emerald-100 text-emerald-700' },
  [SubscriptionStatus.PAST_DUE]: { label: 'Paiement en retard', badge: 'bg-red-100 text-red-700' },
  [SubscriptionStatus.CANCELED]: { label: 'Annulé', badge: 'bg-slate-100 text-slate-500' },
  [SubscriptionStatus.UNPAID]: { label: 'Impayé', badge: 'bg-red-100 text-red-700' },
};

const formatCredit = (cents: number) => `${(cents / 100).toFixed(2).replace('.', ',')} €`;

interface SubscriptionWidgetProps {
  orgId: number;
  plan: SubPlan;
  subscriptionStatus: SubscriptionStatus;
  subscriptionEndDate: string | null;
  subscriptionInterval: string | null;
  cancelAtPeriodEnd: boolean;
  commissionCreditRemainingCents: number;
}

export function SubscriptionWidget({
  orgId,
  plan,
  subscriptionStatus,
  subscriptionEndDate,
  subscriptionInterval,
  cancelAtPeriodEnd,
  commissionCreditRemainingCents,
}: SubscriptionWidgetProps) {
  const [loading, setLoading] = useState<'monthly' | 'annual' | 'portal' | null>(null);
  const [showCardModal, setShowCardModal] = useState(false);
  const [pendingInterval, setPendingInterval] = useState<'monthly' | 'annual' | null>(null);
  const isForfait = plan === SubPlan.FORFAIT;
  const subCfg = SUB_STATUS_CONFIG[subscriptionStatus];
  const billingIssue =
    subscriptionStatus === SubscriptionStatus.PAST_DUE || subscriptionStatus === SubscriptionStatus.UNPAID;

  // Capabilities : statut Forfait + compteurs Commission (concours payants utilisés / restants)
  const { data: capabilities } = useQuery({
    queryKey: ['org-capabilities', orgId],
    queryFn: () => getOrganizationCapabilitiesApi(orgId),
    staleTime: 60_000,
  });
  const commission = capabilities?.commission;
  const quotaSaturated = !!commission && !capabilities?.forfaitActive && !commission.canCreatePaidCompetition;

  async function handleSubscribe(interval: 'monthly' | 'annual') {
    setLoading(interval);
    try {
      await stripeSubscribeApi(orgId, interval);
      window.location.reload();
    } catch {
      setLoading(null);
    }
  }

  async function handlePortal() {
    setLoading('portal');
    try {
      const { portalUrl } = await stripeBillingPortalApi(orgId);
      window.open(portalUrl, '_blank');
    } finally {
      setLoading(null);
    }
  }

  return (
    <div className='rounded-2xl border border-slate-200 bg-white p-6'>
      <h3 className='mb-4 flex items-center gap-2 font-bold text-slate-900'>
        <Zap className='h-5 w-5 text-slate-600' />
        Abonnement BoulesConnect
      </h3>

      {billingIssue && (
        <div className='mb-4 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3'>
          <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-red-600' />
          <div className='text-xs text-red-800'>
            <p className='font-bold'>
              {subscriptionStatus === SubscriptionStatus.PAST_DUE ? 'Paiement en retard' : 'Abonnement impayé'}
            </p>
            <p className='mt-0.5 leading-relaxed'>
              Votre Forfait n&apos;est plus actif. La commission standard (2 % + 0,30 €, min 0,75 €) s&apos;applique aux
              nouveaux concours payants tant que la facture n&apos;est pas réglée. Réglez votre paiement via &laquo;
              Gérer mon abonnement &raquo; pour réactiver le Forfait.
            </p>
          </div>
        </div>
      )}

      {isForfait && !cancelAtPeriodEnd ? (
        <>
          <div className='mb-3 flex items-center justify-between'>
            <span className='flex items-center gap-1.5 rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700'>
              <Sparkles className='h-3.5 w-3.5' />
              Forfait {subscriptionInterval === 'annual' ? 'Annuel' : 'Mensuel'}
            </span>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${subCfg.badge}`}>{subCfg.label}</span>
          </div>
          {subscriptionEndDate && (
            <p className='mb-3 text-xs text-slate-500'>
              Renouvellement le {formatDateAbbreviatedFr(subscriptionEndDate)}
            </p>
          )}
          <button
            onClick={handlePortal}
            disabled={loading !== null}
            className='flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:opacity-60'
          >
            {loading === 'portal' ? <Loader2 className='h-4 w-4 animate-spin' /> : <ExternalLink className='h-4 w-4' />}
            G&eacute;rer mon abonnement
          </button>
        </>
      ) : isForfait && cancelAtPeriodEnd ? (
        <>
          <div className='mb-3 flex items-center justify-between'>
            <span className='flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700'>
              <Sparkles className='h-3.5 w-3.5' />
              Forfait {subscriptionInterval === 'annual' ? 'Annuel' : 'Mensuel'}
            </span>
            <span className='rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700'>
              Annulation programm&eacute;e
            </span>
          </div>
          {subscriptionEndDate && (
            <p className='mb-3 text-xs text-slate-500'>
              Expire le {formatDateAbbreviatedFr(subscriptionEndDate)}. Choisissez comment continuer :
            </p>
          )}
          <div className='space-y-2'>
            <button
              onClick={() => handleSubscribe(subscriptionInterval === 'annual' ? 'annual' : 'monthly')}
              disabled={loading !== null}
              className='flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:opacity-60'
            >
              {loading === (subscriptionInterval === 'annual' ? 'annual' : 'monthly') ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : (
                <Sparkles className='h-4 w-4' />
              )}
              Reprendre le Forfait {subscriptionInterval === 'annual' ? 'Annuel' : 'Mensuel'}
            </button>
            <button
              onClick={() => handleSubscribe(subscriptionInterval === 'annual' ? 'monthly' : 'annual')}
              disabled={loading !== null}
              className='flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-indigo-200 px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-50 disabled:opacity-60'
            >
              {loading === (subscriptionInterval === 'annual' ? 'monthly' : 'annual') ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : (
                <Sparkles className='h-4 w-4' />
              )}
              Passer au Forfait{' '}
              {subscriptionInterval === 'annual'
                ? `Mensuel à ${ORGA_FORFAIT_MONTHLY_TTC_CENTS / 100}€/mois TTC`
                : `Annuel à ${ORGA_FORFAIT_ANNUAL_TTC_CENTS / 100}€/an TTC`}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className='mb-3'>
            <span className='rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600'>
              Commission Partenaire (2 % + 0,30 €, min 0,75 €/inscription)
            </span>
          </div>

          {commission && (
            <div className='mb-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3'>
              <p className='mb-2 text-xs font-semibold text-slate-700'>Concours payants utilisés</p>
              <div className='space-y-2'>
                <div>
                  <div className='flex items-baseline justify-between text-xs text-slate-600'>
                    <span>Ce mois</span>
                    <span className='font-mono font-semibold text-slate-900'>
                      {commission.monthlyUsed} / {commission.monthlyLimit}
                    </span>
                  </div>
                  <div className='mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-200'>
                    <div
                      className={`h-full ${commission.monthlyRemaining === 0 ? 'bg-red-500' : 'bg-indigo-500'}`}
                      style={{
                        width: `${Math.min(100, (commission.monthlyUsed / Math.max(commission.monthlyLimit, 1)) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
                <div>
                  <div className='flex items-baseline justify-between text-xs text-slate-600'>
                    <span>Cette année</span>
                    <span className='font-mono font-semibold text-slate-900'>
                      {commission.yearlyUsed} / {commission.yearlyLimit}
                    </span>
                  </div>
                  <div className='mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-200'>
                    <div
                      className={`h-full ${commission.yearlyRemaining === 0 ? 'bg-red-500' : 'bg-indigo-500'}`}
                      style={{
                        width: `${Math.min(100, (commission.yearlyUsed / Math.max(commission.yearlyLimit, 1)) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
              {quotaSaturated && (
                <p className='mt-3 text-xs font-semibold text-red-700'>
                  Quota atteint &mdash; passez au Forfait pour des concours payants illimités.
                </p>
              )}
              {commission.commissionThisMonthCents > 0 && (
                <div className='mt-3 flex items-baseline justify-between border-t border-slate-200 pt-2.5 text-xs'>
                  <span className='text-slate-600'>Commissions ce mois</span>
                  <span className='font-semibold text-slate-900'>
                    {formatCredit(commission.commissionThisMonthCents)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Upsell : économie réelle vs Forfait (commissions du mois > 80 €) */}
          {commission && commission.commissionThisMonthCents > ORGA_FORFAIT_MONTHLY_TTC_CENTS && (
            <div className='mb-4 flex items-start gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3'>
              <Sparkles className='mt-0.5 h-4 w-4 shrink-0 text-indigo-600' />
              <p className='text-xs leading-relaxed text-indigo-900'>
                Vous auriez économisé{' '}
                <span className='font-bold'>
                  {formatCredit(commission.commissionThisMonthCents - ORGA_FORFAIT_MONTHLY_TTC_CENTS)}
                </span>{' '}
                ce mois-ci avec le Forfait.
              </p>
            </div>
          )}

          {FORFAIT_ENABLED && (
            <>
              <p className='mb-3 text-xs text-slate-500'>
                Passez au Forfait pour 0% de commission, analytics avanc&eacute;s et fonctionnalit&eacute;s premium.
              </p>
              <div className='space-y-2'>
                <button
                  onClick={() => {
                    setPendingInterval('monthly');
                    setShowCardModal(true);
                  }}
                  className='flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-indigo-700'
                >
                  <Sparkles className='h-4 w-4' />
                  Forfait mensuel &mdash; {ORGA_FORFAIT_MONTHLY_TTC_CENTS / 100}&euro;/mois TTC
                </button>
                <button
                  onClick={() => {
                    setPendingInterval('annual');
                    setShowCardModal(true);
                  }}
                  className='flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-indigo-200 px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-50'
                >
                  <Sparkles className='h-4 w-4' />
                  Forfait annuel &mdash; {ORGA_FORFAIT_ANNUAL_TTC_CENTS / 100}&euro;/an TTC
                </button>
              </div>
            </>
          )}
        </>
      )}

      {commissionCreditRemainingCents > 0 && (
        <div className='mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3'>
          <p className='text-xs font-semibold text-emerald-800'>
            Cr&eacute;dit commission : {formatCredit(commissionCreditRemainingCents)}
          </p>
          <p className='mt-0.5 text-xs text-emerald-700'>
            Appliqu&eacute; automatiquement sur vos prochaines commissions
          </p>
        </div>
      )}

      {pendingInterval && (
        <StripeCardModal
          open={showCardModal}
          onClose={() => {
            setShowCardModal(false);
            setPendingInterval(null);
          }}
          interval={pendingInterval}
          planLabel={`Forfait ${pendingInterval === 'annual' ? `Annuel à ${ORGA_FORFAIT_ANNUAL_TTC_CENTS / 100}€/an TTC` : `Mensuel à ${ORGA_FORFAIT_MONTHLY_TTC_CENTS / 100}€/mois TTC`}`}
          onSetupIntent={() => stripeSetupIntentApi(orgId)}
          onSubscribe={async paymentMethodId => {
            const result = await stripeSubscribeApi(orgId, pendingInterval, paymentMethodId);
            return { clientSecret: result.clientSecret };
          }}
          onSuccess={() => {
            setShowCardModal(false);
            setPendingInterval(null);
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}
